import React, { useState, useEffect } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaStar,
} from "react-icons/fa";

const testimonials = [
  {
    id: 1,
    name: "Maria Garcia",
    role: "Language Arts Teacher",
    image:
      "https://storage.googleapis.com/a1aa/image/59379b66-3524-4027-af20-433f2b3470ee.jpg",
    text: "The writing analysis tools help me provide better feedback to my students. It's like having a teaching assistant that helps identify patterns in student writing and suggests targeted improvements and support.",
  },
  {
    id: 2,
    name: "Robert Kim",
    role: "Technology Director",
    image:
      "https://storage.googleapis.com/a1aa/image/9e258d31-1877-43db-2323-3828a9dbf50a.jpg",
    text: "Implementation was seamless, and the platform's reliability has exceeded our expectations. The support team is outstanding, and the regular updates keep adding value to our teaching and learning workflows.",
  },
  {
    id: 3,
    name: "Jennifer Martinez",
    role: "ESL Specialist",
    image:
      "https://storage.googleapis.com/a1aa/image/08be3b64-e4e2-4727-21c7-b5e59bbd5f8f.jpg",
    text: "The language learning tools and automatic differentiation features have revolutionized how we support our ESL students. The AI-powered translations and cultural context suggestions help students thrive.",
  },
  {
    id: 4,
    name: "Derek Wong",
    role: "Math Teacher",
    image:
      "https://storage.googleapis.com/a1aa/image/8a4b1c2d-3c0f-4cbe-bb72-917d44394b6f.jpg",
    text: "The AI math tools help me spot gaps quickly. I love the detailed analytics and targeted recommendations for every student.",
  },
  {
    id: 5,
    name: "Priya Singh",
    role: "Science Department Head",
    image:
      "https://storage.googleapis.com/a1aa/image/9f1f3157-c10e-452d-91b6-84b67f4155ee.jpg",
    text: "Our department has grown more efficient thanks to the intuitive AI resources and the community support. Game changer for science education!",
  },
];

const Testimonials = () => {
  const [visibleCount, setVisibleCount] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expanded, setExpanded] = useState(
    Array(testimonials.length).fill(false)
  );

  // Update visibleCount on screen resize
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(1);
      } else {
        setVisibleCount(3);
      }
    };

    updateVisibleCount(); // run on mount
    window.addEventListener("resize", updateVisibleCount);

    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - visibleCount : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev + visibleCount >= testimonials.length ? 0 : prev + 1
    );
  };

  const toggleExpand = (index) => {
    setExpanded((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  // Get the visible testimonials in window
  const visibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    const index = (currentIndex + i) % testimonials.length;
    visibleTestimonials.push(testimonials[index]);
  }

  return (
    <div
      id="testimonial"
      className="bg-gradient-to-r from-[#e9efff] via-[#f7f5f7] to-[#f7f5f7] min-h-screen lg:mt-[-100px]"
    >
      <main className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 items-center justify-between gap-12">

        <div className="text-center max-w-3xl mx-auto mb-8">
          <h2 className="text-3xl font-extrabold text-[#0f172a] leading-tight">
            Loved by Educators
          </h2>
          <p className="mt-3 text-[#475569] text-lg font-normal">
            Join thousands of educators who are transforming their teaching
            experience
          </p>
        </div>

        <div className="relative flex items-center justify-center">
          {/* Left arrow */}
          <button
            aria-label="Previous testimonial"
            onClick={handlePrev}
            className="absolute left-0 z-10 bg-white shadow-md w-12 h-12 rounded-full flex items-center justify-center text-[#475569] hover:text-[#0f172a] transition"
          >
            <FaChevronLeft className="text-lg" />
          </button>

          {/* Cards container */}
          <div className="flex space-x-6 overflow-hidden">
            {visibleTestimonials.map((testimonial) => (
              <article
                key={testimonial.id}
                className="bg-white rounded-xl shadow-sm p-6 max-w-xs min-w-[280px] flex flex-col justify-between"
              >
                <div>
                  <div className="flex space-x-1 mb-3">
                    {Array(5)
                      .fill(0)
                      .map((_, i) => (
                        <FaStar key={i} className="text-yellow-400" />
                      ))}
                  </div>
                  <p className="text-[#475569] italic text-sm leading-relaxed">
                    {expanded[testimonial.id - 1]
                      ? testimonial.text
                      : testimonial.text.slice(0, 120) + "..."}
                  </p>
                  <button
                    aria-expanded={expanded[testimonial.id - 1]}
                    onClick={() => toggleExpand(testimonial.id - 1)}
                    className="mt-3 text-blue-600 text-sm font-medium flex items-center space-x-1"
                  >
                    <span>
                      {expanded[testimonial.id - 1] ? "Show Less" : "Show More"}
                    </span>
                    <FaChevronDown className="text-xs" />
                  </button>
                </div>
                <div className="flex items-center mt-6 space-x-3">
                  <img
                    src={testimonial.image}
                    alt={`Portrait of ${testimonial.name}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-[#0f172a] font-semibold text-sm leading-tight">
                      {testimonial.name}
                    </p>
                    <p className="text-[#64748b] text-xs leading-tight">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Right arrow */}
          <button
            aria-label="Next testimonial"
            onClick={handleNext}
            className="absolute right-0 z-10 bg-white shadow-md w-12 h-12 rounded-full flex items-center justify-center text-[#475569] hover:text-[#0f172a] transition"
          >
            <FaChevronRight className="text-lg" />
          </button>
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center mt-8 space-x-3">
          {testimonials.map((_, index) => (
            <span
              key={index}
              className={`rounded-full ${
                index === currentIndex ? "w-6 bg-[#2563eb]" : "w-3 bg-[#cbd5e1]"
              } h-3 transition-all`}
            ></span>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Testimonials;